import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, User, LogOut, Calendar, Clock, Crown,
  Shield, Star, Flame, Loader2, Check, Zap, Coins
} from 'lucide-react';

export default function ProfilePage({ onNavigate }) {
  const { api, user, logout, refreshUser } = useAuth();
  const [badges, setBadges] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [examDate, setExamDate] = useState(user?.exam_date?.split('T')[0] || '');
  const [className, setClassName] = useState(user?.class_name || 'Class 8');
  const [dailyGoal, setDailyGoal] = useState(user?.daily_goal_minutes || 15);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [badgeRes, planRes] = await Promise.all([
        api.get('/gamification/badges'),
        api.get('/payment/plans'),
      ]);
      setBadges(badgeRes.data);
      setPlans(planRes.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.put('/auth/profile', {
        class_name: className,
        daily_goal_minutes: parseInt(dailyGoal),
        exam_date: examDate ? new Date(examDate).toISOString() : null,
      });
      setSaved(true);
      await refreshUser();
      setTimeout(() => setSaved(false), 2000);
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const badgeIcons = { school: Shield, flame: Flame, video: Star, trophy: Crown, star: Star, rocket: Zap, crown: Crown };

  return (
    <div className="pb-nav px-4 pt-6 max-w-lg mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button data-testid="back-from-profile" onClick={() => onNavigate('dashboard')} className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold text-white font-['Outfit']" data-testid="profile-title">Profile</h1>
        </div>
        <button
          data-testid="logout-btn"
          onClick={logout}
          className="flex items-center gap-1 text-slate-400 hover:text-red-400 text-sm transition-colors"
        >
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </div>

      {/* User Card */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-violet-600/20 flex items-center justify-center">
            <User className="w-7 h-7 text-violet-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white font-['Outfit']" data-testid="profile-name">{user?.name}</h2>
            <p className="text-xs text-slate-500">{user?.email} • {user?.role === 'parent' ? 'Parent' : user?.role === 'teacher' ? 'Teacher' : `${user?.class_name || 'Student'}`}</p>
          </div>
        </div>
        
        {/* Stats - Only for Students */}
        {user?.role === 'student' && (
          <div className="grid grid-cols-3 gap-3 mt-4">
            <div className="text-center bg-slate-800/30 rounded-xl py-3">
              <p className="text-lg font-bold text-violet-400 font-['Outfit']">{user?.xp || 0}</p>
              <p className="text-[10px] text-slate-500">XP</p>
            </div>
            <div className="text-center bg-slate-800/30 rounded-xl py-3">
              <p className="text-lg font-bold text-yellow-400 font-['Outfit']">{user?.coins || 0}</p>
              <p className="text-[10px] text-slate-500">Coins</p>
            </div>
            <div className="text-center bg-slate-800/30 rounded-xl py-3">
              <p className="text-lg font-bold text-orange-400 font-['Outfit']">{user?.streak || 0}</p>
              <p className="text-[10px] text-slate-500">Streak</p>
            </div>
          </div>
        )}
        
        {/* Parent Stats */}
        {user?.role === 'parent' && (
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="text-center bg-slate-800/30 rounded-xl py-3">
              <p className="text-lg font-bold text-violet-400 font-['Outfit']">{user?.linked_children?.length || 0}</p>
              <p className="text-[10px] text-slate-500">Linked Children</p>
            </div>
            <div className="text-center bg-slate-800/30 rounded-xl py-3">
              <p className="text-lg font-bold text-cyan-400 font-['Outfit']">{user?.subscription || 'free'}</p>
              <p className="text-[10px] text-slate-500">Plan</p>
            </div>
          </div>
        )}
      </div>

      {/* Settings - Only for Students */}
      {user?.role === 'student' && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5 space-y-4">
          <h3 className="text-sm font-semibold text-white">Settings</h3>
          
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">Class</label>
            <select
              data-testid="profile-class-select"
              value={className}
              onChange={e => setClassName(e.target.value)}
              className="w-full bg-slate-950/50 border border-slate-800 focus:border-violet-500 rounded-xl h-11 px-3 text-sm text-slate-100 outline-none appearance-none"
            >
              {['Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10', 'Class 11', 'Class 12'].map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">Exam Date</label>
            <div className="relative">
              <Calendar className="absolute left-3 top-3 w-4 h-4 text-slate-600" />
              <input
                data-testid="exam-date-input"
                type="date"
                value={examDate}
                onChange={e => setExamDate(e.target.value)}
                className="w-full bg-slate-950/50 border border-slate-800 focus:border-violet-500 rounded-xl h-11 pl-10 pr-3 text-sm text-slate-100 outline-none [color-scheme:dark]"
              />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">
              Daily Goal: {dailyGoal} minutes
            </label>
            <input
              data-testid="daily-goal-slider"
              type="range"
              min="5"
              max="60"
              step="5"
              value={dailyGoal}
              onChange={e => setDailyGoal(e.target.value)}
            className="w-full accent-violet-500"
          />
          <div className="flex justify-between text-[10px] text-slate-600">
            <span>5m</span><span>15m</span><span>30m</span><span>60m</span>
          </div>
        </div>

        <button
          data-testid="save-profile-btn"
          onClick={saveProfile}
          disabled={saving}
          className={`w-full rounded-xl h-11 font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
            saved ? 'bg-emerald-600 text-white' : 'bg-violet-600 hover:bg-violet-700 text-white'
          } disabled:opacity-50`}
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <Check className="w-4 h-4" /> : null}
          {saved ? 'Saved!' : 'Save Settings'}
        </button>
        </div>
      )}

      {/* Badges */}
      {badges.length > 0 && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5">
          <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <Crown className="w-4 h-4 text-yellow-400" /> Badges
          </h3>
          <div className="grid grid-cols-2 gap-2">
            {badges.map(b => {
              const Icon = badgeIcons[b.icon] || Star;
              return (
                <div key={b.id} className={`p-3 rounded-xl border ${
                  b.earned ? 'border-yellow-500/30 bg-yellow-500/5' : 'border-slate-800 bg-slate-950/30 opacity-50'
                }`} data-testid={`badge-${b.id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className={`w-4 h-4 ${b.earned ? 'text-yellow-400' : 'text-slate-600'}`} />
                    <span className="text-xs font-medium text-white">{b.name}</span>
                  </div>
                  <p className="text-[10px] text-slate-500">{b.description}</p>
                  <p className="text-[10px] text-violet-400 mt-1">{b.xp} XP</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Subscription Plans */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5">
        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
          <Zap className="w-4 h-4 text-violet-400" /> Subscription Plans
        </h3>
        <div className="space-y-3">
          {plans.map(plan => (
            <div key={plan.id} className={`p-4 rounded-xl border transition-all ${
              plan.id === 'pro_6month' ? 'border-violet-500/30 bg-violet-500/5' : 'border-slate-800'
            }`} data-testid={`plan-${plan.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h4 className="text-sm font-bold text-white flex items-center gap-2">
                    {plan.name}
                    {plan.id === 'pro_6month' && <span className="text-[10px] bg-violet-600 text-white px-2 py-0.5 rounded-full">Popular</span>}
                  </h4>
                  <p className="text-xs text-slate-500">{plan.period}</p>
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold text-white font-['Outfit']">₹{plan.price}</p>
                  <p className="text-[10px] text-slate-500">/{plan.period}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-1 mb-3">
                {plan.features.map((f, i) => (
                  <span key={i} className="text-[10px] bg-slate-800/60 text-slate-400 px-2 py-0.5 rounded-full">{f}</span>
                ))}
              </div>
              <button
                data-testid={`subscribe-${plan.id}`}
                className={`w-full rounded-lg py-2 text-xs font-medium transition-all ${
                  user?.subscription === plan.id
                    ? 'bg-emerald-600/20 text-emerald-400 border border-emerald-500/30'
                    : 'bg-violet-600 hover:bg-violet-700 text-white'
                }`}
              >
                {user?.subscription === plan.id ? 'Current Plan' : 'Subscribe'}
              </button>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-slate-600 text-center mt-3">Payment integration coming soon (Razorpay)</p>
      </div>
    </div>
  );
}
